import {
  useGetSubscription,
  useListBillingPlans,
  usePreviewStripeInvoice,
} from "@/api/stripe";
import {
  BillingInterval,
  PreviewStripeInvoicePayload,
  StripeInvoicePreview,
  StripeInvoicePreviewLine,
} from "@cohesive/api-client";
import { useCallback, useEffect, useMemo } from "react";
import { useDebounceCallback } from "usehooks-ts";

export interface InvoicePreviewParams {
  planName: string;
  billingInterval: BillingInterval;
  seatCount: number;
  enabled?: boolean; // Whether to trigger the preview
}

export interface InvoicePreviewResult {
  /** Current total price based on preview or fallback calculation */
  totalPrice: number;
  /** Price per seat for display */
  pricePerSeat: number;
  /** Discount amount if applicable */
  discount: number;
  /** Whether the preview is currently loading */
  isLoading: boolean;
  /** Error from the preview API call */
  error: Error | null;
  /** Raw preview data from the API */
  previewData: StripeInvoicePreview | null;
  /** Line items from the invoice preview for detailed billing breakdown */
  lineItems: StripeInvoicePreviewLine[];
  /** Function to manually trigger preview */
  triggerPreview: (params: InvoicePreviewParams) => void;
  /** Whether user has a default payment method for API calls */
  hasDefaultPaymentMethod: boolean;
  /** Whether the values are from API data or fallback calculations */
  isUsingFallback: boolean;
}

export interface UseInvoicePreviewOptions {
  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number;
  /** Whether to automatically trigger preview on param changes (default: true) */
  autoPreview?: boolean;
}

/**
 * Custom hook for managing Stripe invoice previews with debouncing and fallback calculations
 */
export function useInvoicePreview(
  params: InvoicePreviewParams,
  options: UseInvoicePreviewOptions = {},
): InvoicePreviewResult {
  const { debounceMs = 500, autoPreview = true } = options;

  const { data: subscription } = useGetSubscription();
  const { data: billingPlans } = useListBillingPlans();
  const previewInvoice = usePreviewStripeInvoice();

  // Check if user has a default payment method
  const hasDefaultPaymentMethod = Boolean(
    subscription?.data.stripe_subscription?.default_payment_method_id,
  );

  // Calculate plan pricing from billing plans data
  const planPricing = useMemo((): {
    monthlyPrice?: number;
    yearlyPrice?: number;
    availableIntervals: BillingInterval[];
  } | null => {
    if (!billingPlans?.data.billing_plans || !params.planName) return null;

    const monthlyPlan = billingPlans.data.billing_plans.find(
      (plan) =>
        plan.name === params.planName && plan.billing_interval === "month",
    );
    const yearlyPlan = billingPlans.data.billing_plans.find(
      (plan) =>
        plan.name === params.planName && plan.billing_interval === "year",
    );

    const availableIntervals: BillingInterval[] = [];
    const result: {
      monthlyPrice?: number;
      yearlyPrice?: number;
      availableIntervals: BillingInterval[];
    } = { availableIntervals };

    if (monthlyPlan) {
      result.monthlyPrice = monthlyPlan.seat_price / 100; // Convert from cents to dollars
      availableIntervals.push("month");
    }

    if (yearlyPlan) {
      result.yearlyPrice = yearlyPlan.seat_price / 100; // Convert from cents to dollars
      availableIntervals.push("year");
    }

    // Return null if no plans found
    if (availableIntervals.length === 0) return null;

    return result;
  }, [billingPlans?.data.billing_plans, params.planName]);

  // Debounced preview function
  const debouncedPreviewInvoice = useDebounceCallback(
    useCallback(
      (previewParams: InvoicePreviewParams) => {
        if (!previewParams.enabled) return;

        // Only make API call if there's a default payment method and the billing interval is available
        if (!hasDefaultPaymentMethod) return;
        if (
          !planPricing?.availableIntervals.includes(
            previewParams.billingInterval,
          )
        )
          return;

        const payload: PreviewStripeInvoicePayload = {
          plan_name: previewParams.planName,
          billing_interval: previewParams.billingInterval,
          seat_count: previewParams.seatCount,
          trial_end_now: false, // Don't end trial in preview
        };

        previewInvoice.mutate(payload);
      },
      [previewInvoice, hasDefaultPaymentMethod, planPricing],
    ),
    debounceMs,
    {
      leading: true,
    },
  );

  // Manual trigger function
  const triggerPreview = useCallback(
    (triggerParams: InvoicePreviewParams) => {
      debouncedPreviewInvoice(triggerParams);
    },
    [debouncedPreviewInvoice],
  );

  // Auto-trigger preview when params change (if enabled)
  useEffect(() => {
    if (autoPreview && params.enabled) {
      debouncedPreviewInvoice(params);
    }
  }, [autoPreview, ...Object.values(params)]);

  // Extract preview data - only use if we have a payment method
  const previewData: StripeInvoicePreview | null = hasDefaultPaymentMethod
    ? previewInvoice.data?.data.stripe_invoice_preview || null
    : null;

  // Only show loading state if we have a payment method and are actually making API calls
  const isLoading = hasDefaultPaymentMethod && previewInvoice.isPending;

  // Calculate fallback pricing
  const calculateFallbackPricing = () => {
    if (!planPricing) return { totalPrice: 0, pricePerSeat: 0 };

    let pricePerSeat = 0;

    if (params.billingInterval === "year" && planPricing.yearlyPrice) {
      pricePerSeat = planPricing.yearlyPrice;
    } else if (params.billingInterval === "month" && planPricing.monthlyPrice) {
      pricePerSeat = planPricing.monthlyPrice;
    }

    return {
      totalPrice: pricePerSeat * params.seatCount,
      pricePerSeat: pricePerSeat,
    };
  };

  const fallback = calculateFallbackPricing();

  // Only use preview data if it's valid (non-zero and not loading)
  const previewTotalPrice = previewData ? previewData.amount_due / 100 : 0;
  const hasValidPreview =
    hasDefaultPaymentMethod && !isLoading && previewTotalPrice > 0;

  const totalPrice = hasValidPreview ? previewTotalPrice : fallback.totalPrice;
  // Always use plan pricing for price per seat, not divided total (due to prorating)
  const pricePerSeat = fallback.pricePerSeat;

  // Calculate discount from preview data or fallback
  const discount =
    previewData?.starting_balance && hasValidPreview
      ? Math.abs(previewData.starting_balance / 100)
      : planPricing &&
          planPricing.monthlyPrice &&
          planPricing.yearlyPrice &&
          params.billingInterval === "year"
        ? (planPricing.monthlyPrice * 12 - planPricing.yearlyPrice) *
          params.seatCount
        : 0;

  // Extract line items from preview data
  const lineItems: StripeInvoicePreviewLine[] = previewData?.lines || [];

  return {
    totalPrice,
    pricePerSeat,
    discount,
    isLoading,
    error: previewInvoice.error,
    previewData,
    lineItems,
    triggerPreview,
    hasDefaultPaymentMethod,
    isUsingFallback: !hasValidPreview,
  };
}
